import * as React from "react";
import OtpInput from "react-otp-input";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTimeout } from "react-use";
import { clsx } from "clsx";
import { CircleLoader } from "@/components/ui/circleLoader";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LoginFormInputs } from "@/views/auth/login/types";

interface TwoFactorFormProps {
  isLoading: boolean;
  error: string | null;
  resendCode: (channel?: "email" | "sms") => void;
}

export const TwoFactorForm: React.FC<TwoFactorFormProps> = ({ resendCode, isLoading, error }) => {
  const [isResendCodeEnabled, , resetIsResendCodeEnabled] = useTimeout(30000);
  const { control } = useFormContext<LoginFormInputs>();
  const code = useWatch({
    control,
    name: "code",
  });
  const channel = useWatch({
    control,
    name: "channel",
  });

  const handleResendCode = async (selectedChannel: "email" | "sms") => {
    await resendCode(selectedChannel);
    resetIsResendCodeEnabled();
  };

  return (
    <>
      <h2 className="mb-4 text-lg font-medium text-ui-paragraph-text">
        Enter the code sent by {channel === "sms" ? "SMS" : "Email"}.
      </h2>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div className="flex justify-center">
        <Controller
          control={control}
          name="code"
          render={({ field }) => (
            <OtpInput
              skipDefaultStyles={true}
              value={code}
              numInputs={6}
              shouldAutoFocus={true}
              renderInput={props => (
                <input
                  {...props}
                  disabled={isLoading}
                  className="mx-1 block w-1/6 appearance-none rounded-md border border-input px-4 py-3 text-center placeholder-muted shadow-sm focus:border-primary focus:outline-none focus:ring-primary sm:text-lg"
                />
              )}
              onChange={field.onChange}
            />
          )}
        />
      </div>

      <div className="mt-6">
        <button
          type="submit"
          disabled={isLoading || (code || "").length !== 6}
          className="flex w-full justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isLoading ? (
            <>
              <CircleLoader /> Verifying...
            </>
          ) : (
            "Submit"
          )}
        </button>
      </div>

      <div className="mt-4 text-center">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={clsx("text-sm font-medium text-primary", {
                "hover:underline": isResendCodeEnabled(),
                "cursor-pointer": isResendCodeEnabled(),
              })}
              disabled={!isResendCodeEnabled()}
            >
              Resend code by SMS or Email
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => handleResendCode("sms")} className="cursor-pointer">
              Resend code by SMS
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleResendCode("email")} className="cursor-pointer">
              Resend code by Email
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
};
